import { SearchItem } from '@/templates/DataNames';
import { createBreadcrumbs, expandTree } from '@/heplers/index';

export interface RootNamesInterface {
  key?: string;
}

export type SearchResultsType = {
  [T in RootNamesInterface[keyof RootNamesInterface]]: SearchItem[];
};

type Segment =
  | { type: 'text'; content: string }
  | { type: 'code'; content: string }
  | { type: 'link'; text: string; url: string }
  | { type: 'htmlTag'; content: string };

type ProcessedSegment = {
  type: 'text';
  content: string;
};

/**
 *  Создаем объект с референсами второго уровня. Для этого дерево, превращенное в массив, фильтруем по глубине fqdn,
 *  и потом превращает в обьект с видом [value.fqdn]: value.title
 *
 * @return обьект вида [value.fqdn]: value.title. Например 'references.iossdk' = 'IOS SDK'
 */
export function computeRootNames(tree: any) {
  const RootNames = expandTree(tree)
    .filter((value: any) => {
      const splittedFqdn = value.fqdn.split('.');
      return splittedFqdn.length === 2 && splittedFqdn[0] === 'references';
    })
    .reduce((acc: RootNamesInterface, currentValue: any) => {
      return {
        ...acc,
        ...{ [currentValue.fqdn]: currentValue.title },
      };
    }, {});
  RootNames['guides'] = 'Guides';
  RootNames['getting started'] = 'Getting started';
  RootNames['gettingstarted'] = 'Getting started';
  RootNames['getting-started'] = 'Getting started';
  RootNames['references'] = 'References';
  RootNames['tutorials'] = 'Tutorials';
  RootNames['channels'] = 'Channels';
  RootNames['contactcenter'] = 'Contact center';
  RootNames['settings'] = 'Settings';
  RootNames['integrations'] = 'Integrations';
  RootNames['security'] = 'Security';
  RootNames['dialingcampaigns'] = 'Dialing campaigns';
  RootNames['editor'] = 'Editor';
  RootNames['basicconcepts'] = 'Basic concepts';
  RootNames['history'] = 'History';
  RootNames['administration'] = 'Administration';
  RootNames['setup'] = 'Setup';
  RootNames['workspace'] = 'Workspace';

  return RootNames;
}

/**
 *  Группируем массив результата поиска по референсам, которые достали в методе computeRootNames
 *
 * @return SearchResultsType
 */
export function treatmentSearchData<T extends SearchResultsType>(
  data: any[],
  inputString: string,
  RootNames: RootNamesInterface,
  tree?: any
): T {
  const newSearch = (objAcc: T, objValue: SearchItem) => {
    const sdkKey: RootNamesInterface[keyof RootNamesInterface] =
      //@ts-expect-error: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'RootNamesInterface'.
      RootNames[objValue.root_fqdn] ?? 'Other';

    const newValue = {
      ...objValue,
      description: makeHighLightHelper(objValue.description, inputString),
      breadcrumbs: tree ? createBreadcrumbs(tree, objValue.fqdn) : null,
    };

    return objAcc[sdkKey]
      ? //@ts-expect-error: number is not object
        { ...objAcc, ...objAcc[sdkKey].push(newValue) }
      : { ...objAcc, ...{ [sdkKey]: [newValue] } };
  };

  let newData: any = [];
  if (data[0].cat)
    // Переводим в одномерный массив объектов
    newData = data.reduce((acc, value) => acc.concat(value.results), []);
  else newData = data;

  return newData.reduce(newSearch, {});
}

export function makeHighLightHelper(
  value: Record<string, string> | string,
  inputString: string
): Record<string, string> | string {
  if (typeof value === 'object') {
    Object.keys(value).forEach((key) => {
      value[key] = replaceOutsideSpecialSegments(value[key], inputString);
    });
    return value;
  } else {
    return replaceOutsideSpecialSegments(value, inputString);
  }
}

function replaceOutsideSpecialSegments(text: string, inputString: string): string {
  // Regular expression to match Markdown links, inline code, and HTML tags
  const regex = /(`[^`]*`)|(\[([^\]]+)]\(([^)]+)\))|(<[^>]+>)/g;

  const segments: Segment[] = [];
  let lastIndex = 0;
  let match;

  // Loop through all matches
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      // Text before the current match
      const beforeMatch = text.slice(lastIndex, match.index);
      segments.push({ type: 'text', content: beforeMatch });
    }

    if (match[1]) {
      // Inline code detected
      segments.push({ type: 'code', content: match[1] });
    } else if (match[2]) {
      // Markdown link detected
      segments.push({
        type: 'link',
        text: match[3], // Link text
        url: match[4], // Link URL
      });
    } else if (match[5]) {
      // HTML tag detected
      segments.push({
        type: 'htmlTag',
        content: match[5],
      });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    // Remaining text after the last match
    const afterLastMatch = text.slice(lastIndex);
    segments.push({ type: 'text', content: afterLastMatch });
  }

  // Process each segment accordingly
  const processedSegments: ProcessedSegment[] = segments.map((segment) => {
    if (segment.type === 'text') {
      // Replace in normal text
      return {
        type: 'text',
        content: replaceAllOccurrences(segment.content, inputString),
      };
    } else if (segment.type === 'link') {
      // Replace in link text but not in URL
      const newLinkText = replaceAllOccurrences(segment.text, inputString);
      return {
        type: 'text',
        content: `[${newLinkText}](${segment.url})`,
      };
    } else {
      // Leave code, HTML tags, and other types unmodified
      return { type: 'text', content: segment.content };
    }
  });

  // Concatenate all segments to form the processed text
  return processedSegments.map((segment) => segment.content || '').join('');
}

function replaceAllOccurrences(str: string, search: string): string {
  // Escape special characters for regex
  const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escapedSearch, 'gi');
  return str.replace(regex, '**$&**');
}
